// ── Patrimia API Hooks ──
// Pattern identique a proptax — DRY via meme structure

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// ── Dev role header (synced with RoleContext) ──
// In dev mode, the backend reads X-Dev-Role to impersonate profiles.
let _devRole = 'AGENT'
export function setDevRole(role: string) { _devRole = role }

// ── Client fetch de base ──

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  // In dev, send the active role so backend can impersonate the right user
  if (import.meta.env.DEV) {
    headers['X-Dev-Role'] = _devRole
  }

  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.error, body.message)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message || code)
  }
}

// ── Hooks types ──

interface PaginatedResponse<T> {
  data: T[]
  pagination: { page: number; limit: number; total: number; pages: number }
}

export function useApiQuery<T>(path: string, options?: { enabled?: boolean }) {
  return useQuery<T, ApiError>({
    queryKey: [path],
    queryFn: () => api<T>(path),
    ...options,
  })
}

export function useApiList<T>(path: string, params?: Record<string, string | number | undefined>) {
  const qs = params
    ? '?' + Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
        .join('&')
    : ''
  const url = `${path}${qs}`

  return useQuery<PaginatedResponse<T>, ApiError>({
    queryKey: [path, params],
    queryFn: () => api<PaginatedResponse<T>>(url),
  })
}

export function useApiMutation<TBody, TResult = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
) {
  const queryClient = useQueryClient()

  return useMutation<TResult, ApiError, TBody>({
    mutationFn: (body) =>
      api<TResult>(path, {
        method,
        body: body ? JSON.stringify(body) : undefined,
      }),
    onSuccess: () => {
      const basePath = path.split('/').slice(0, 3).join('/')
      queryClient.invalidateQueries({ queryKey: [basePath] })
    },
  })
}

export function useApiAction<TBody = void, TResult = unknown>(method: 'POST' | 'PATCH' | 'DELETE' = 'POST') {
  const queryClient = useQueryClient()

  return useMutation<TResult, ApiError, { url: string; body?: TBody }>({
    mutationFn: ({ url, body }) =>
      api<TResult>(url, {
        method,
        body: body ? JSON.stringify(body) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries()
    },
  })
}
